import { createContext, useContext, ReactNode } from 'react';
import { useWorkplaceDetails } from '@/hooks/business/useWorkplaceDetails';
import { useAuth } from '@/hooks/useAuth';
import { getCountryByCode } from '@/utils/countries';

interface WorkplaceCurrencyContextValue {
  currencyCode: string;
  isLoading: boolean;
}

const WorkplaceCurrencyContext = createContext<WorkplaceCurrencyContextValue | undefined>(undefined);

export function WorkplaceCurrencyProvider({ children }: { children: ReactNode }) {
  const { user, profile, currentCompany } = useAuth();
  const hasCompanyAssociation = !!(user && profile && currentCompany);
  
  const { data: workplaceDetails, isLoading } = useWorkplaceDetails({ 
    enabled: hasCompanyAssociation 
  });

  // Resolution order:
  // 1. Explicit currency_code on company_settings (user-chosen).
  // 2. Derive from country_code via the canonical countries map
  //    (fixes legacy rows where currency_code was never persisted).
  // 3. Fall back to USD only if neither is available.
  const derivedFromCountry = workplaceDetails?.country_code
    ? getCountryByCode(workplaceDetails.country_code)?.currency
    : undefined;
  const currencyCode =
    workplaceDetails?.currency_code || derivedFromCountry || 'USD';

  return (
    <WorkplaceCurrencyContext.Provider value={{ currencyCode, isLoading }}>
      {children}
    </WorkplaceCurrencyContext.Provider>
  );
}

export function useWorkplaceCurrency() {
  const context = useContext(WorkplaceCurrencyContext);
  if (context === undefined) {
    throw new Error('useWorkplaceCurrency must be used within a WorkplaceCurrencyProvider');
  }
  return context;
}